import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Avatar,
  Chip,
  Stack,
  Divider,
  CircularProgress,
  IconButton,
  Tooltip,
  Paper,
  useTheme,
  useMediaQuery,
  Alert,
} from '@mui/material';
import {
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Work as WorkIcon,
  AccountBalance as BankIcon,
  Badge as BadgeIcon,
  CalendarToday as CalendarIcon,
  Edit as EditIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { request } from '../../../../../../utils/request';
import Brandcrumb from '../../../../../../componentsUiBlock/Brandcrumb';
import { Employee } from '../types';
import { baseUrlImage } from '../../../../../../utils/apiClient';

const EmployeeDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [employee, setEmployee] = useState<Employee | null>();
  const [loading, setLoading] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  // const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const fetchEmployeeDetails = useCallback(async () => {
    try {
      setLoading(true);
      const res = await request({
        url: `employee/get-single-employee/${id}`,
        method: 'get',
      });

      if (res.status === 'success' && res.data) {
        setEmployee(res.data);
      } else {
        toast.error('Employee not found');
      }
    } catch (error: any) {
      toast.error(error || 'Failed to fetch employee details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchEmployeeDetails();
  }, [id, fetchEmployeeDetails]);

  // Format date to readable string
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Calculate tenure
  const calculateTenure = (joiningDate: string) => {
    const joinDate = new Date(joiningDate);
    const today = new Date();

    const years = today.getFullYear() - joinDate.getFullYear();
    const months = today.getMonth() - joinDate.getMonth();

    let totalMonths = years * 12 + months;
    if (today.getDate() < joinDate.getDate()) {
      totalMonths--;
    }

    const tenureYears = Math.floor(totalMonths / 12);
    const tenureMonths = totalMonths % 12;

    if (tenureYears === 0 && tenureMonths === 0) {
      return 'Less than a month';
    }

    const parts = [];
    if (tenureYears > 0) {
      parts.push(`${tenureYears} year${tenureYears > 1 ? 's' : ''}`);
    }
    if (tenureMonths > 0) {
      parts.push(`${tenureMonths} month${tenureMonths > 1 ? 's' : ''}`);
    }

    return parts.join(', ');
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case "active":
        return "success";
      case "pending":
        return "warning";
      case "suspended":
        return "error";
      case "deactivated":
        return "default";
      case "deleted":
        return "error";
      default:
        return "default";
    }
  };

  const getStatusMessage = (status?: string) => {
    switch (status) {
      case "active":
        return "";
      case "pending":
        return "Please verify your email to activate your account and access the system.";
      case "suspended":
        return "Your account has been suspended. Please contact the administrator for assistance.";
      case "deactivated":
        return "Your account has been deactivated. Please contact the administrator to reactivate your account.";
      case "deleted":
        return "This account no longer exists. Please contact the administrator if you believe this is a mistake.";
      default:
        return "default";
    }
  };

  // Responsive layout helper
  const renderTwoColumnLayout = (leftContent: React.ReactNode, rightContent: React.ReactNode) => {
    if (isMobile || isTablet) {
      return (
        <Stack spacing={2}>
          {leftContent}
          {rightContent}
        </Stack>
      );
    }

    return (
      <Box display="flex" gap={2} alignItems="flex-start">
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {leftContent}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {rightContent}
        </Box>
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading employee details...</Typography>
      </Box>
    );
  }

  if (!employee) {
    return (
      <Box sx={{ textAlign: 'center', mt: 4 }}>
        <Typography variant="h6" color="text.secondary">
          Employee not found
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate("/shopManagement/employeeManagement/employees")}
          sx={{ mt: 2 }}
          size={isMobile ? 'small' : 'medium'}
        >
          Back to Employees List
        </Button>
      </Box>
    );
  }

  const fullName = `${employee?.first_name} ${employee?.last_name}`;
  const tenure = calculateTenure(employee?.job.joining_date);

  return (
    <>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <Brandcrumb />
        <Chip
          onClick={() => navigate(-1)}
          icon={<ArrowBackIosNewIcon sx={{ fontSize: "14px" }} />}
          label="Back"
          variant="filled"
          color="secondary"
          sx={{
            fontSize: '14px',
            color: 'text.secondary',
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: 'action.hover'
            }
          }}
        />
      </Box>

      <Card elevation={4}>
        <CardContent sx={{ p: isMobile ? 1 : 2 }}>
          {/* Employee Profile Header */}
          <Box sx={{ p: isMobile ? 1.5 : 2, mb: 2, bgcolor: 'primary.lighter', borderRadius: 1.5 }}>
            <Box display="flex" alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" gap={1.5}>
              {/* Left side with avatar and basic info */}
              <Box display="flex" alignItems="center" gap={1.5}>
                <Avatar
                  src={ baseUrlImage + employee?.photo}
                  sx={{
                    bgcolor: 'primary.main',
                    width: isMobile ? 50 : 60,
                    height: isMobile ? 50 : 60,
                    border: '2px solid white',
                    flexShrink: 0
                  }}
                >
                  <PersonIcon sx={{ fontSize: isMobile ? 24 : 32 }} />
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Box display="flex" alignItems="center" gap={0.5} mb={0.5} flexWrap="wrap">
                    <Typography
                      variant={isMobile ? "h6" : "h5"}
                      fontWeight="bold"
                      noWrap
                      sx={{ fontSize: isMobile ? '1.1rem' : '1.25rem' }}
                    >
                      {fullName}
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={0.5} mb={0.5} flexWrap="wrap">
                    {employee?.userId?.userName && <Chip
                      label={"User Name : " + employee?.userId?.userName}
                      color="secondary"
                      variant="filled"
                      size="small"
                      sx={{ height: isMobile ? 20 : 24, fontSize: isMobile ? '0.6rem' : '0.75rem' }}
                    />}

                    {employee?.userId?.role?.name && <Chip
                      label={"Role : " + employee?.userId?.role?.name}
                      color="info"
                      variant="outlined"
                      size="small"
                      sx={{ height: isMobile ? 20 : 24, fontSize: isMobile ? '0.6rem' : '0.75rem' }}
                    />}

                    {employee?.userId?.status && (
                      <Chip
                        label={"User Status : " + employee?.userId?.status}
                        color={getStatusColor(employee?.userId?.status) as any}
                        size="small"
                        sx={{
                          height: isMobile ? 20 : 24,
                          fontSize: isMobile ? "0.6rem" : "0.75rem"
                        }}
                      />
                    )}

                    <Chip
                      label={employee?.job.employee_type}
                      color="primary"
                      variant="outlined"
                      size="small"
                      sx={{ height: isMobile ? 20 : 24, fontSize: isMobile ? '0.6rem' : '0.75rem' }}
                    />
                  </Box>
                  {employee?.userId?.status === 'pending' ? <Alert severity="warning">{getStatusMessage(employee?.userId?.status)}</Alert>
                    : ''}
                </Box>
              </Box>

              {/* Right side with action buttons */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', mt: isMobile ? 1 : 0 }}>
                <Tooltip title="Check Assigned Enquiry" arrow>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => navigate('/enquiry', {
                      state: {
                        employeeId: employee._id,
                        source: 'employee'
                      }
                    })}
                    size="small"
                    sx={{ minWidth: isMobile ? 120 : 'auto' }}
                  >
                    {isMobile ? 'Enquiry' : 'Check Assigned Enquiry'}
                  </Button>
                </Tooltip>
                <Tooltip title="Check Monthly Attendance" arrow>
                  <Button
                    variant="outlined"
                    size='small'
                    onClick={() => navigate(`/shopManagement/employeeManagement/attendance/${employee?._id}`)}
                    sx={{ minWidth: isMobile ? 90 : 'auto' }}
                  >
                    {isMobile ? 'Attendance' : 'Attendance'}
                  </Button>
                </Tooltip>
                <Tooltip title="Edit Employee" arrow>
                  <IconButton
                    color="primary"
                    onClick={() => navigate(`/shopManagement/employeeManagement/employees/editEmployee/${employee?._id}`)}
                    size="small"
                  >
                    <EditIcon fontSize={isMobile ? "small" : "medium"} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Box>

          {/* Main Content - Responsive Layout */}
          {renderTwoColumnLayout(
            /* Left Column */
            <Stack spacing={1.5}>
              {/* Personal Information Card */}
              <Card elevation={1}>
                <CardContent sx={{ p: 1.5 }}>
                  <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <PersonIcon color="primary" fontSize="small" />
                    Personal Information
                  </Typography>

                  <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', flexWrap: 'wrap', gap: 1.5 }}>
                    <Box sx={{ flex: isMobile ? '1 1 100%' : '1 1 45%', minWidth: 0 }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        First Name
                      </Typography>
                      <Typography variant="body2" fontWeight="medium" noWrap>
                        {employee?.first_name}
                      </Typography>
                    </Box>
                    <Box sx={{ flex: isMobile ? '1 1 100%' : '1 1 45%', minWidth: 0 }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Last Name
                      </Typography>
                      <Typography variant="body2" fontWeight="medium" noWrap>
                        {employee?.last_name}
                      </Typography>
                    </Box>
                    <Box sx={{ flex: isMobile ? '1 1 100%' : '1 1 45%', minWidth: 0 }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Date of Birth
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {employee?.dob ? formatDate(employee?.dob) : 'Not provided'}
                      </Typography>
                    </Box>
                    <Box sx={{ flex: isMobile ? '1 1 100%' : '1 1 45%', minWidth: 0 }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Gender
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {employee?.gender}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              {/* Contact Information Card */}
              <Card elevation={1}>
                <CardContent sx={{ p: 1.5 }}>
                  <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <PhoneIcon color="primary" fontSize="small" />
                    Contact Information
                  </Typography>

                  <Stack spacing={1.5}>
                    {/* Phone Numbers */}
                    <Box>
                      <Typography variant="caption" color="text.secondary" gutterBottom>
                        Phone Numbers
                      </Typography>
                      <Stack spacing={0.75}>
                        {employee?.contact.phone.map((phone, index) => (
                          <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PhoneIcon fontSize="small" color="action" />
                            <Typography variant="body2" fontSize="0.875rem">
                              {phone.label}: {phone.phone_no}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    </Box>

                    {/* Email Addresses */}
                    <Box>
                      <Typography variant="caption" color="text.secondary" gutterBottom>
                        Email Addresses
                      </Typography>
                      <Stack spacing={0.75}>
                        {employee?.contact.email.map((email, index) => (
                          <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <EmailIcon fontSize="small" color="action" />
                            <Typography variant="body2" fontSize="0.875rem" noWrap>
                              {email}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>

              {/* Address Information Card */}
              <Card elevation={1}>
                <CardContent sx={{ p: 1.5 }}>
                  <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <LocationIcon color="primary" fontSize="small" />
                    Address Information
                  </Typography>

                  <Stack spacing={1.5}>
                    {/* Address Line 1 */}
                    {employee?.address.line1 && (
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Address Line
                        </Typography>
                        <Typography variant="body2" fontWeight="medium" fontSize="0.875rem">
                          {employee?.address.line1}
                        </Typography>
                      </Box>
                    )}

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                      {/* City */}
                      {employee?.address.city && (
                        <Box sx={{ flex: '1 1 45%', minWidth: 0 }}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            City
                          </Typography>
                          <Typography variant="body2" fontWeight="medium" fontSize="0.875rem">
                            {employee?.address.city}
                          </Typography>
                        </Box>
                      )}

                      {/* State */}
                      {employee?.address.state && (
                        <Box sx={{ flex: '1 1 45%', minWidth: 0 }}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            State
                          </Typography>
                          <Typography variant="body2" fontWeight="medium" fontSize="0.875rem">
                            {employee?.address.state}
                          </Typography>
                        </Box>
                      )}

                      {/* Country */}
                      {employee?.address.country && (
                        <Box sx={{ flex: '1 1 45%', minWidth: 0 }}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Country
                          </Typography>
                          <Typography variant="body2" fontWeight="medium" fontSize="0.875rem">
                            {employee?.address.country}
                          </Typography>
                        </Box>
                      )}

                      {/* PIN Code */}
                      {employee?.address.pinCode && (
                        <Box sx={{ flex: '1 1 45%', minWidth: 0 }}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            PIN Code
                          </Typography>
                          <Typography variant="body2" fontWeight="medium" fontFamily="monospace" fontSize="0.875rem">
                            {employee?.address.pinCode}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Stack>
                </CardContent>
              </Card>

              {/* Job Information Card */}
              <Card elevation={1}>
                <CardContent sx={{ p: 1.5 }}>
                  <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <WorkIcon color="primary" fontSize="small" />
                    Job Information
                  </Typography>

                  <Stack spacing={1.5}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                      <Box sx={{ flex: '1 1 45%', minWidth: 0 }}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Employee Type
                        </Typography>
                        <Typography variant="body2" fontWeight="medium" fontSize="0.875rem">
                          {employee?.job.employee_type}
                        </Typography>
                      </Box>
                      <Box sx={{ flex: '1 1 45%', minWidth: 0 }}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Joining Date
                        </Typography>
                        <Typography variant="body2" fontWeight="medium" fontSize="0.875rem">
                          {formatDate(employee?.job.joining_date)}
                        </Typography>
                      </Box>
                    </Box>

                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Tenure
                      </Typography>
                      <Typography variant="body2" fontWeight="medium" fontSize="0.875rem">
                        {tenure}
                      </Typography>
                    </Box>

                    <Divider sx={{ my: 0.5 }} />

                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Base Salary (Monthly)
                      </Typography>
                      <Typography variant="h6" fontWeight="bold" color="primary">
                        {formatCurrency(employee?.job.base_salary)}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>

              {/* Financial Information Card */}
              <Card elevation={1}>
                <CardContent sx={{ p: 1.5 }}>
                  <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <BankIcon color="primary" fontSize="small" />
                    Financial Information
                  </Typography>

                  <Stack spacing={1.5}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                      {employee?.finance?.bank_ac_no && (
                        <Box sx={{ flex: '1 1 45%', minWidth: 0 }}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Bank Account
                          </Typography>
                          <Typography variant="body2" fontWeight="medium" fontFamily="monospace" fontSize="0.875rem">
                            {employee?.finance?.bank_ac_no}
                          </Typography>
                        </Box>
                      )}

                      {employee?.finance?.ifsc_code && (
                        <Box sx={{ flex: '1 1 45%', minWidth: 0 }}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            IFSC Code
                          </Typography>
                          <Typography variant="body2" fontWeight="medium" fontFamily="monospace" fontSize="0.875rem">
                            {employee?.finance?.ifsc_code}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Stack>,

            /* Right Column */
            <Stack spacing={1.5}>
              {/* Document Information Card */}
              <Card elevation={1}>
                <CardContent sx={{ p: 1.5 }}>
                  <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <BadgeIcon color="primary" fontSize="small" />
                    Document Information
                  </Typography>

                  <Stack spacing={1.5}>
                    {/* Aadhaar Section */}
                    {employee?.document?.aadhaar && (
                      <>
                        <Box>
                          <Typography variant="caption" color="text.secondary" gutterBottom>
                            Aadhaar Details
                          </Typography>

                          {/* Aadhaar Number */}
                          {employee?.document.aadhaar.aadhaar_no && (
                            <Box sx={{ mb: 1 }}>
                              <Typography variant="caption" color="text.secondary" display="block">
                                Aadhaar Number
                              </Typography>
                              <Typography variant="body2" fontWeight="medium" fontFamily="monospace" fontSize="0.875rem">
                                {employee?.document.aadhaar.aadhaar_no}
                              </Typography>
                            </Box>
                          )}

                          {/* Aadhaar Photos */}
                          {(employee?.document.aadhaar.aadhaar_photo_front || employee?.document.aadhaar.aadhaar_photo_back) && (
                            <Box>
                              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                Aadhaar Photos
                              </Typography>

                              <Box sx={{ display: 'flex', gap: 1, flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
                                {/* Front Photo Card */}
                                {employee?.document.aadhaar.aadhaar_photo_front && (
                                  <Paper
                                    elevation={0}
                                    sx={{
                                      p: 1,
                                      border: '1px solid',
                                      borderColor: 'divider',
                                      borderRadius: 0.5,
                                      flex: 1,
                                      minWidth: 100
                                    }}
                                  >
                                    <Typography variant="body2" fontWeight="medium" sx={{ mb: 0.5, fontSize: '0.75rem' }}>
                                      Front Side
                                    </Typography>
                                    <Box
                                      sx={{
                                        cursor: 'pointer',
                                        '&:hover': { opacity: 0.8 }
                                      }}
                                      onClick={() => window.open(baseUrlImage+employee?.document?.aadhaar?.aadhaar_photo_front, '_blank')}
                                    >
                                      <Avatar
                                        src={baseUrlImage + employee?.document.aadhaar.aadhaar_photo_front}
                                        sx={{
                                          width: "100%",
                                          height: "auto",
                                          aspectRatio: '1.58', // Aadhaar card aspect ratio
                                          margin: '0 auto',
                                          border: '1px solid',
                                          borderColor: 'divider',
                                          borderRadius: 0
                                        }}
                                      >
                                        <ImageIcon />
                                      </Avatar>
                                      <Typography
                                        variant="caption"
                                        color="primary"
                                        sx={{
                                          display: 'block',
                                          textAlign: 'center',
                                          mt: 0.5,
                                          fontSize: '0.65rem'
                                        }}
                                      >
                                        View
                                      </Typography>
                                    </Box>
                                  </Paper>
                                )}

                                {/* Back Photo Card */}
                                {employee?.document.aadhaar.aadhaar_photo_back && (
                                  <Paper
                                    elevation={0}
                                    sx={{
                                      p: 1,
                                      border: '1px solid',
                                      borderColor: 'divider',
                                      borderRadius: 0.5,
                                      flex: 1,
                                      minWidth: 100
                                    }}
                                  >
                                    <Typography variant="body2" fontWeight="medium" sx={{ mb: 0.5, fontSize: '0.75rem' }}>
                                      Back Side
                                    </Typography>
                                    <Box
                                      sx={{
                                        cursor: 'pointer',
                                        '&:hover': { opacity: 0.8 }
                                      }}
                                      onClick={() => window.open(baseUrlImage+employee?.document?.aadhaar?.aadhaar_photo_back, '_blank')}
                                    >
                                      <Avatar
                                        src={baseUrlImage+ employee?.document.aadhaar.aadhaar_photo_back}
                                        sx={{
                                          width: "100%",
                                          height: "auto",
                                          aspectRatio: '1.58',
                                          margin: '0 auto',
                                          border: '1px solid',
                                          borderColor: 'divider',
                                          borderRadius: 0
                                        }}
                                      >
                                        <ImageIcon />
                                      </Avatar>
                                      <Typography
                                        variant="caption"
                                        color="primary"
                                        sx={{
                                          display: 'block',
                                          textAlign: 'center',
                                          mt: 0.5,
                                          fontSize: '0.65rem'
                                        }}
                                      >
                                        View
                                      </Typography>
                                    </Box>
                                  </Paper>
                                )}
                              </Box>
                            </Box>
                          )}
                        </Box>
                      </>
                    )}

                    {/* PAN Section */}
                    {employee?.document?.pan && (
                      <>
                        <Divider sx={{ my: 0.5 }} />
                        <Box>
                          <Typography variant="caption" color="text.secondary" gutterBottom>
                            PAN Details
                          </Typography>
                          <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 1.5, mt: 0.5 }}>
                            {employee?.document.pan.pan_no && (
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  PAN Number
                                </Typography>
                                <Typography variant="body2" fontWeight="medium" fontFamily="monospace" fontSize="0.875rem">
                                  {employee?.document.pan.pan_no}
                                </Typography>
                              </Box>
                            )}

                            {employee?.document.pan.pan_photo && (
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  PAN Photo
                                </Typography>
                                <Paper
                                  elevation={0}
                                  sx={{
                                    p: 1,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 0.5,
                                    mt: 0.5
                                  }}
                                >
                                  <Box
                                    sx={{
                                      cursor: 'pointer',
                                      '&:hover': { opacity: 0.8 }
                                    }}
                                    onClick={() => window.open(baseUrlImage+employee?.document?.pan?.pan_photo, '_blank')}
                                  >
                                    <Avatar
                                      src={baseUrlImage + employee?.document?.pan?.pan_photo}
                                      sx={{
                                        width: "100%",
                                        height: "auto",
                                        aspectRatio: '1.25', 
                                        margin: '0 auto',
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: 0
                                      }}
                                    >
                                      <ImageIcon />
                                    </Avatar>
                                    <Typography
                                      variant="caption"
                                      color="primary"
                                      sx={{
                                        display: 'block',
                                        textAlign: 'center',
                                        mt: 0.5,
                                        fontSize: '0.65rem'
                                      }}
                                    >
                                      View
                                    </Typography>
                                  </Box>
                                </Paper>
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </>
                    )}

                    {!employee?.document?.aadhaar && !employee?.document?.pan && (
                      <Typography variant="body2" color="text.secondary" fontSize="0.875rem">
                        No document information available
                      </Typography>
                    )}
                  </Stack>
                </CardContent>
              </Card>

              {/* System Information Card */}
              <Card elevation={1}>
                <CardContent sx={{ p: 1.5 }}>
                  <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <CalendarIcon color="primary" fontSize="small" />
                    System Information
                  </Typography>

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                    <Box sx={{ flex: '1 1 45%', minWidth: 0 }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Created On
                      </Typography>
                      <Typography variant="body2" fontSize="0.875rem">
                        {formatDate(employee?.createdAt)}
                      </Typography>
                    </Box>
                    <Box sx={{ flex: '1 1 45%', minWidth: 0 }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Last Updated
                      </Typography>
                      <Typography variant="body2" fontSize="0.875rem">
                        {formatDate(employee?.updatedAt)}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Stack>
          )}
        </CardContent>
      </Card>
    </>
  );
};

export default EmployeeDetail;